import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('Branding');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);
import {
  BrandingTheme,
  HeaderLayout,
  AuthConfig,
  UiOverrides,
  CampaignUiConfig,
  DEFAULT_BRANDING_THEME,
  DEFAULT_HEADER_LAYOUT,
  DEFAULT_AUTH_CONFIG,
} from '@/types/branding';
import { setBrandingPrimaryColor } from '@/hooks/useBrandingOverrides';
import { hexToRgba } from '@/utils/colorUtils';

const DEFAULT_ROW_HEIGHT = 56;

interface BrandingContextType {
  theme: BrandingTheme;
  headerLayout: HeaderLayout;
  authConfig: AuthConfig;
  uiOverrides: UiOverrides;
  headerHeight: number;
  isLoading: boolean;
  primaryColor: string;
  isLightHeader: boolean;
  isAuthMethodEnabled: (method: keyof AuthConfig) => boolean;
  primaryWithOpacity: (opacity: number) => string;
}

const defaultContext: BrandingContextType = {
  theme: DEFAULT_BRANDING_THEME,
  headerLayout: DEFAULT_HEADER_LAYOUT,
  authConfig: DEFAULT_AUTH_CONFIG,
  uiOverrides: {},
  headerHeight: DEFAULT_ROW_HEIGHT,
  isLoading: true,
  primaryColor: DEFAULT_BRANDING_THEME.primary_color,
  isLightHeader: false,
  isAuthMethodEnabled: () => true,
  primaryWithOpacity: (opacity: number) => hexToRgba(DEFAULT_BRANDING_THEME.primary_color, opacity),
};

const BrandingContext = createContext<BrandingContextType>(defaultContext);

export function useBranding() {
  return useContext(BrandingContext);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

function computeHeaderHeight(layout: HeaderLayout): number {
  if (!layout?.rows?.length) return DEFAULT_ROW_HEIGHT;
  return layout.rows.reduce((total, row) => {
    if (row.is_separator) return total + (row.separator_thickness || 1);
    return total + (row.height || DEFAULT_ROW_HEIGHT);
  }, 0);
}

function mergeWithDefaults<T extends Record<string, any>>(partial: Partial<T> | null | undefined, defaults: T): T {
  if (!partial) return defaults;
  const result = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (partial[key] !== undefined && partial[key] !== null) {
      (result as any)[key] = partial[key];
    }
  }
  return result;
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { campaignCountriesLanguagesId, isReady: campaignReady } = useCampaignConfig();
  const [config, setConfig] = useState<CampaignUiConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const fetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!campaignReady) {
      return;
    }

    if (!campaignCountriesLanguagesId) {
      setIsLoading(false);
      return;
    }

    if (fetchedIdRef.current === campaignCountriesLanguagesId) {
      return;
    }

    fetchedIdRef.current = campaignCountriesLanguagesId;

    const fetchConfig = async () => {
      try {
        const { data, error } = await supabase
          .from('campaign_ui_config')
          .select('*')
          .eq('campaign_countries_languages_id', campaignCountriesLanguagesId)
          .maybeSingle();

        if (error) {
          const isTableMissing = error.code === 'PGRST205' || error.code === '42P01';
          if (isTableMissing) {
            logWithTimestamp('[Branding] Table campaign_ui_config not yet created, using defaults');
          } else {
            errorWithTimestamp('[Branding] Error fetching UI config:', error);
          }
          setIsLoading(false);
          return;
        }

        if (data) {
          setConfig(data as CampaignUiConfig);
          logWithTimestamp('[Branding] Config loaded for campaign:', campaignCountriesLanguagesId);
        } else {
          logWithTimestamp('[Branding] No custom config found, using defaults');
        }
      } catch (err) {
        logWithTimestamp('[Branding] Fetch failed, using defaults');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [campaignReady, campaignCountriesLanguagesId]);

  const value = useMemo<BrandingContextType>(() => {
    const theme = mergeWithDefaults(config?.theme, DEFAULT_BRANDING_THEME);
    const headerLayout = config?.header_layout?.rows?.length
      ? config.header_layout
      : DEFAULT_HEADER_LAYOUT;
    const authConfig = mergeWithDefaults(config?.auth_config, DEFAULT_AUTH_CONFIG);
    const uiOverrides = config?.ui_overrides || {};
    const headerHeight = computeHeaderHeight(headerLayout);

    const rgb = hexToRgb(theme.primary_color);
    const primaryRgb = rgb || { r: 255, g: 27, b: 141 };

    setBrandingPrimaryColor(theme.primary_color);

    return {
      theme,
      headerLayout,
      authConfig,
      uiOverrides,
      headerHeight,
      isLoading,
      primaryColor: theme.primary_color,
      isLightHeader: theme.background_mode === 'light',
      isAuthMethodEnabled: (method: keyof AuthConfig) => authConfig[method] ?? true,
      primaryWithOpacity: (opacity: number) =>
        `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, ${opacity})`,
    };
  }, [config, isLoading]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}
